import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/artistes", label: "Artistes" },
  { to: "/comment-ca-marche", label: "Comment ça marche" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const dashboardPath =
    user && user.role === "admin"
      ? "/admin"
      : user && user.role === "artist"
      ? "/espace-artiste"
      : "/espace-client";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#C9A24B]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-3 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-sm tracking-wide transition-colors hover:text-[#C9A24B] ${
                location.pathname === l.to ? "text-[#C9A24B]" : "text-[#D8D5CD]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate("/artistes")}
            data-testid="nav-reserver-btn"
            className="hidden sm:inline-flex gold-bg text-black font-semibold tracking-wide hover:opacity-90 rounded-full px-6"
          >
            Réserver
          </Button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="w-10 h-10 rounded-full gold-border flex items-center justify-center text-[#C9A24B] hover:bg-[#C9A24B]/10 transition"
                >
                  <User size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#15151b] border-[#C9A24B]/20 text-white w-56">
                <DropdownMenuLabel className="text-[#A19E95]">
                  {user.name}
                  <div className="text-xs text-[#C9A24B] uppercase tracking-wider">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="menu-dashboard"
                  onClick={() => navigate(dashboardPath)}
                  className="cursor-pointer focus:bg-[#C9A24B]/15"
                >
                  <LayoutDashboard size={16} className="mr-2" /> Mon espace
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="cursor-pointer focus:bg-[#C9A24B]/15"
                >
                  <LogOut size={16} className="mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigate("/connexion")}
              data-testid="nav-login-btn"
              className="hidden sm:inline-flex border-[#C9A24B]/50 text-[#C9A24B] hover:bg-[#C9A24B]/10 bg-transparent rounded-full"
            >
              Connexion
            </Button>
          )}

          <button
            className="lg:hidden text-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-[#C9A24B]/20 px-6 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="text-[#D8D5CD] hover:text-[#C9A24B]"
            >
              {l.label}
            </Link>
          ))}
          {!user && (
            <Link to="/connexion" onClick={() => setOpen(false)} className="text-[#C9A24B]">
              Connexion
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
